import { useEffect, useState, useRef } from 'react';
import { useLocation } from 'react-router-dom';

/**
 * PageTransition Component
 * 
 * Wraps page content with a subtle fade-in animation on route changes.
 * Creates a polished, premium feel for navigation transitions.
 * Works on both desktop and mobile devices.
 */
export default function PageTransition({ children }) {
  const location = useLocation();
  const [isVisible, setIsVisible] = useState(true);
  const isFirstRender = useRef(true);
  const prevLocation = useRef(location.pathname + location.search);

  useEffect(() => {
    const currentLocation = location.pathname + location.search;
    
    // Skip animation on first render
    if (isFirstRender.current) {
      isFirstRender.current = false;
      prevLocation.current = currentLocation;
      return;
    }

    // Only animate if location actually changed
    if (prevLocation.current !== currentLocation) {
      prevLocation.current = currentLocation;
      
      // Fade out
      setIsVisible(false);
      
      // Fade in after short delay
      const timer = setTimeout(() => {
        setIsVisible(true);
      }, 50);

      return () => clearTimeout(timer);
    }
  }, [location.pathname, location.search]);

  return (
    <div
      className="page-transition"
      style={{
        opacity: isVisible ? 1 : 0,
        transform: isVisible ? 'translateY(0)' : 'translateY(8px)',
        transition: 'opacity 250ms ease-out, transform 250ms ease-out',
        willChange: 'opacity, transform',
        WebkitTransform: isVisible ? 'translateY(0)' : 'translateY(8px)',
        WebkitTransition: '-webkit-transform 250ms ease-out, opacity 250ms ease-out',
      }}
    >
      {children}
    </div>
  );
}
